import React, { ReactNode, useState } from "react";
import { Badge } from "flowbite-react";
import { DriverAvatar } from "@src/common/components/Avatar";
import classNames from "classnames";
import PeekDriverTasks from "./PeekDriverTasks";
import { Driver, DriverPaySummary } from "@api/graphql/generated/generated-types";
import { MAX_WIDTH_TO_HANDLE_IN_BETWEEN_NEGATIVE_SPACE_IN_LARGE_SCREEN } from "../constants/styleConstants";
import { LabelBadge } from "@src/common/components/LabelManagement/components";
import { useCourierLocale } from "@src/common/courierLocale";

type Props = {
  children?: React.ReactNode;
  title: string;
  fullName?: string;
  avatarDisplay?: string | undefined;
  subText: ReactNode;
  badgeText?: string;
  /** "gray" for exclusion tags; omit for green (success) */
  badgeColor?: "success" | "gray";
  image: string;
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onClick: () => void;
  showBorder?: boolean;
  isKeyboardSelected?: boolean;
  driver: Driver;
  showAssignedLabels?: boolean;
  showDriverPay?: boolean;
  showDailyEarnings?: boolean;
  dailyEarningsSummary?: DriverPaySummary | null;
};

export function ExpandableDriverCard({
  title,
  fullName = "",
  subText,
  badgeText,
  badgeColor,
  image,
  expanded,
  onExpand,
  onCollapse,
  onClick,
  children,
  showBorder = false,
  isKeyboardSelected = false,
  driver,
  showAssignedLabels = false,
  showDriverPay = false,
  showDailyEarnings = false,
  dailyEarningsSummary,
}: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { formatCurrency } = useCourierLocale();
  const driverPaySummary = driver.driverPaySummary;
  const shouldRenderDriverPay = showDriverPay && driverPaySummary != null;
  const shouldRenderDailyEarnings =
    showDailyEarnings && dailyEarningsSummary != null;
  const assignedLabels = driver.assignedLabels ?? [];
  const shouldRenderLabels =
    showAssignedLabels && assignedLabels.length > 0;
  return (
    <div
      className={classNames({
        "overflow-hidden pl-2 pr-2 mb-1 mx-auto": true,
        "bg-blue-50 dark:bg-blue-900/20": isKeyboardSelected,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        maxWidth: MAX_WIDTH_TO_HANDLE_IN_BETWEEN_NEGATIVE_SPACE_IN_LARGE_SCREEN,
      }}
    >
      <div
        className={`${
          expanded || isKeyboardSelected
            ? "bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30"
            : "bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
        } flex flex-col py-2 pl-2 pr-2 rounded-md transition-colors duration-150`}
      >
        <div className="flex-1 flex items-center">
          <div className="flex-1 flex items-center">
            <div style={{ width: 40 }}>
              {isHovered ? (
                // eslint-disable-next-line react/jsx-no-undef
                (<PeekDriverTasks driver={driver} />)
              ) : (
                <DriverAvatar
                  driverPhoto={image}
                  driverName={fullName || title}
                  avatarDisplay={driver.avatarDisplay || undefined}
                  size={35}
                  isActive={showBorder}
                />
              )}
            </div>
            <div className="flex-1 ml-3 cursor-pointer" onClick={onClick}>
              <div className="flex-1 flex items-center truncate">
                <div className="flex-1 text-slate-500 dark:text-gray-300 truncate text-base">
                  <span className="font-bold">{title}</span>
                  {driver.vehicleType && (
                    <span className="font-mono">
                      {" "}
                      {driver.vehicleType.shortCode}
                    </span>
                  )}
                </div>
                {badgeText && (
                  <div className="flex flex-1">
                    <Badge
                      size="xs"
                      color={badgeColor ?? "success"}
                      className="flex-initial ml-2"
                    >
                      {badgeText}
                    </Badge>
                  </div>
                )}
              </div>
              <span className="text-gray-500 dark:text-gray-400 text-xs truncate">{subText}</span>
              {shouldRenderDriverPay && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {formatCurrency(driverPaySummary.totalPay)} ·{" "}
                  {driverPaySummary.numOrders === 1
                    ? "1 order"
                    : `${driverPaySummary.numOrders} orders`}
                </div>
              )}
              {shouldRenderDailyEarnings && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {formatCurrency(dailyEarningsSummary.totalPay)}
                  {driver.dailyEarningTarget != null && (
                    <> / {formatCurrency(driver.dailyEarningTarget)} target</>
                  )}{" "}
                  · today
                </div>
              )}
              {shouldRenderLabels && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {assignedLabels.map((label) => (
                    <LabelBadge key={label.id} label={label} size="xs" />
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* <ExpandChevronIcon
            expanded={expanded}
            onExpand={onExpand}
            onCollapse={onCollapse}
          /> */}
        </div>

        {expanded && <div className="flex-1">{children}</div>}
      </div>
    </div>
  );
}
